import { useLazyQuery, useQuery } from "@apollo/client"
import moment from "moment"
import { PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Heading, Text, Flex, Box, Card, Button } from "rebass/styled-components"
import styled from "styled-components"
import { Skeleton } from "../components/loadingSkeleton/Skeleton"
import { OpenedConversationsContext } from "../contexts/openConversationsCounts"
import { GET_CALENDAR_FOR_MONTH, GET_DASHBOARD_CARDS_DATA, GET_LAST_SEENS, GET_TODAYS_UPCOMINGS, GET_UPCOMINGS } from "../graphql/queries"
import { IGenerateCircleSoCallbackInput } from "../graphql/types/mutations/community"
import { IGetCalendarForMonthOutput, IGetTodaysUpcomingInput, IGetTodaysUpcomingOutput, IGetUpcominsInput, IGetUpcominsOutput, ILastSeenInput, ILastSeenOutput } from "../graphql/types/queries/appointment"
import { IGetDashboardCardsDataInput, IGetDashboardCardsDataOutput } from "../graphql/types/queries/dashboard"
import { IUser } from "../graphql/types/queries/user"
import withSession from "../lib/withSession"
import { Constants } from "../utils/Constants"
import CalendarGrid from "./dashboard/CalendarGrid"
import { LastSeen } from "./dashboard/LastSeen"
import MobileCalendar from "./dashboard/MobileCalendar"
import { Upcomings } from "./dashboard/Upcomings"
import { MessagesListNoSearch } from "./messages/MessagesList"
import YourMetrics from "./dashboard/YourMetrics"
import { PasswordChange } from "./PasswordChange"

// TODO: [SUBSCRIPTIONS example
// const MESSAGE_SUBSCRIPTION = gql`
//   subscription newMessage {
//     newMessage {
//       id
//       type
//       email
//     }
//   }
// `;

interface IDashboard {
  session: IUser
}
const TopCard: React.FC<{ count?: number, text: string, loading: boolean, onClick?: () => void } & PropsWithChildren> = ({ count, children, text, loading, onClick }) => {
  return (
    <Card paddingY={['8px', '30px', '30px']} paddingX={'26px'} marginBottom={['8px', 0, 0]} marginRight={[0, '32px', '32px']} style={{ flex: 'initial', cursor: !!onClick ? 'pointer' : '' }} onClick={() => {
      if (!!onClick) {
        onClick()
      }
    }}>
      <Flex alignItems={'center'}>
        {loading ? (
          <Skeleton height={27} width={40} />
        ) : (
          <Flex alignItems={'baseline'}>
            <Heading mr={2}>{count}</Heading>
            {children}
          </Flex>
        )}
      </Flex>
      <Text mt={2} style={{ fontWeight: 400, color: 'rgba(52, 79, 122, 0.6)', fontSize: '16px' }}>{text}</Text>
    </Card>
  )
}
const Divider = styled(Box)`
  width: 100%;
  height: 1px;
  background-color: #C9C9C9;
  margin-top: 25px;
  margin-bottom: 25px;
`

const Container = styled(Flex)`
  padding: ${() => `${Constants.OUTLETS_PADDING_MOBILE}px`};
  padding-top: ${() => `${Constants.OUTLETS_PADDING_TOP_MOBILE}px`};
  width: 100%;
  padding-bottom: 100px;
  @media screen and (min-width: 68.8125em) {
  padding: ${() => `${Constants.OUTLETS_PADDING}px`};
  padding-top:0;
  padding-bottom: 0;
  min-width: ${() => `${Constants.OUTLETS_WIDTH}px`};
  /* padding-bottom: 0; */
  }
`
const CalendarMobile = styled(Flex)`
/* display: flex;
@media screen and (min-width: 68.8125em) {
  display: none !important;
} */
  
`
const CalendarDesktop = styled(Flex)`
flex:1;
/* display: flex;
@media screen and (max-width: 68.8125em) {
  display: none !important;
} */
  
`
const MetricsWrap = styled(Flex)`
display: flex;
@media screen and (max-width: 1760px) {
  display: none !important;
}
  
`

const Wrapper = styled(Flex)`
  overflow: auto;
  flex-direction: column;
  flex: 1;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;

const Dashboard = ({ session }: IDashboard) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(moment().format('MM/DD/YYYY'))
  const [bypassScroll, setBypassScroll] = useState(true)
  const upcomingsRef = useRef<any>(null)
  const navigate = useNavigate()
  const openedConversationsCount = useContext(OpenedConversationsContext)
  const [loadUpcomings, { data: dataUpcoming, loading: loadingUpcoming, error: errorUpcoming, refetch: refetchUpcoming }] = useLazyQuery<IGetUpcominsOutput, IGetUpcominsInput>(GET_UPCOMINGS)
  const { data: dataTodayUpcoming, loading: loadingTodayUpcoming, error: errorTodayUpcoming, refetch: refetchTodayUpcoming } = useQuery<IGetTodaysUpcomingOutput, IGetTodaysUpcomingInput>(GET_TODAYS_UPCOMINGS)
  const { data: dataDashboardCards, loading: loadingDashboardCards, error: errorDashboardCards, refetch: refetchDashboardCards } = useQuery<IGetDashboardCardsDataOutput, IGetDashboardCardsDataInput>(GET_DASHBOARD_CARDS_DATA)

  // TODO: [SUBSCRIPTIONS example
  // const { data, loading } = useSubscription(MESSAGE_SUBSCRIPTION,{
  //   onData:(e)=>{
  //     console.log('{onData:}', e)
  //   },

  // });
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    loadUpcomings({
      variables: {
        day: selectedDate
      }
    })
  }, [])

  const isMac = useMemo(() => {
    const nav = navigator as Navigator & {
      userAgentData?: {
        platform?: string;
      };
    };

    if (nav.userAgentData && nav.userAgentData.platform) {
      return nav.userAgentData.platform.toLowerCase().includes('mac');
    }

    // Fallback for older browsers
    return navigator.userAgent.toLowerCase().includes('mac');
  }, []);

  // useEffect(() => {
  //   loadUpcomings({
  //     variables: {
  //       day: selectedDate
  //     }
  //   })
  // }, [selectedDate])

  const onDateSelection = (date: string | null) => {
    if (date !== selectedDate && upcomingsRef && upcomingsRef.current) {
      upcomingsRef.current.scrollIntoView()
    } else {
      setBypassScroll(false)
    }
    setSelectedDate(date)
  }

  const refetchUpcomings = () => {
    // refetchUpcoming()
  }

  return (
    <Container flexDirection={'column'} style={{ height: '100%', maxHeight: '100%' }} overflowY={['hidden']}>
      <Flex overflowY={['hidden']} height={'100%'}>
        <Flex flexDirection={'column'} flex={1} overflowY={['hidden']}>
          {/* <Wrapper > */}
          <Heading mb={3} marginTop={[0, '30px', '30px']}>{`Hi ${session.displayName}`}</Heading>
          <Flex flexDirection={['column', 'row', 'row']} display={['none !important', 'flex !important', 'flex !important']}>
            <Flex>
              <Text style={{ fontWeight: 400, color: 'rgba(52, 79, 122, 0.6)' }}>{`You have `}&nbsp;</Text>

              <>
                {loadingTodayUpcoming ? (<Skeleton width={40} />) : (
                  <Box style={{ background: '#FFE3D8', borderRadius: '10px', padding: '0 8px' }}>
                    <Flex>
                      {((dataTodayUpcoming?.healthCoachGetTodaysUpcomings || {}).events || []).length > 0 ?
                        <Text>{dataTodayUpcoming?.healthCoachGetTodaysUpcomings.events.length}&nbsp;</Text> : ''}
                      <Text>{`${((dataTodayUpcoming?.healthCoachGetTodaysUpcomings || {}).events || []).length === 0 ? 'no calls' : (((dataTodayUpcoming?.healthCoachGetTodaysUpcomings || {}).events || []).length || 0) < 2 ? 'call' : 'calls'}`}</Text>
                    </Flex>
                  </Box>
                )}
              </>
            </Flex>
            <Text style={{ fontWeight: 400, color: 'rgba(52, 79, 122, 0.6)' }}>&nbsp;{` scheduled today and`}&nbsp;</Text>
            <Box style={{ background: '#FFE3D8', borderRadius: '10px', padding: '0 8px', cursor: 'pointer', flex: 'initial' }} mt={[1, 0, 0]} onClick={() => navigate('/messages')}>
              <Flex>
                <Text>{openedConversationsCount}&nbsp;</Text>
                <Text>{` ${openedConversationsCount < 2 ? 'message' : 'messages'} `}</Text>
              </Flex>
            </Box>
          </Flex>

          <Flex flexDirection={['column', 'row', 'row']} mt={4} mb={[0, 2, 2]} justifyContent="flex-start">

          </Flex>
          <Divider />
          <Flex flexDirection={['column-reverse', 'row', 'row']} mt={0} mb={[4, 0]} id='mainCol' overflow={'hidden'}>
            <Box ref={upcomingsRef} overflow={"hidden"} display={'flex'} minWidth={'300px'}>
              <Upcomings data={dataUpcoming} loading={loadingUpcoming} error={errorUpcoming} refetch={refetchUpcomings} selectedDate={selectedDate} />
            </Box>
            <Flex flexDirection={'column'} ml={3} overflow={'hidden'}>
              <Flex mb={'20px'}>
                <Heading style={{ fontSize: '24px' }} flex={1}>Your open conversations</Heading>
                <Button onClick={() => navigate('/messages')} variant={'outline'} style={{ width: 'initial', fontSize: '14px' }}>Go to inbox</Button>
              </Flex>
              <Wrapper>
                <MessagesListNoSearch onSelectConversation={() => { }} isEmbed />
              </Wrapper>
            </Flex>
          </Flex>
          {/* </Wrapper> */}
        </Flex>



        <MetricsWrap ml={3} flex={1} style={{ position: 'sticky', top: 0, padding: '40px', paddingRight: 0, borderRadius: '70px', background: 'rgba(30, 70, 212, 0.15)', height: 'calc(100vh - 51px)', overflowY: 'hidden', maxHeight: 'calc(100vh - 51px)', maxWidth: '600px', marginTop: '20px' }}>
          <YourMetrics dataDashboardCards={dataDashboardCards} loadingDashboardCards={loadingDashboardCards} dataUpcoming={dataUpcoming} loadingUpcoming={loadingUpcoming} />
        </MetricsWrap>

        {visible && (
          <Flex style={{
            position: 'absolute',
            top: '0px',
            background: '#4a4a4a',
            padding: '8px 22px',
            left: '50%',
            border: '3px solid white',
            borderTop: '0px',
            borderRadius: '0 0 12px 12px',
            marginLeft: '-225px'
          }} alignItems={'center'} justifyContent={'center'}>
            <Text style={{ color: 'white', fontWeight: 300, fontSize: '13px', opacity: 0.95 }}>Press</Text>
            <Box style={{
              color: 'white',
              fontWeight: 500, fontSize: '13px',
              background: 'linear-gradient(358deg, rgb(153 153 153), transparent)',
              border: '1px solid #eee',
              marginLeft: '5px',
              marginRight: '5px',
              borderRadius: '5px',
              opacity: 0.75
            }} paddingX={2} paddingY={1}>
              {!!isMac ? 'Ctrl' : 'Alt'}
            </Box>
            <Text style={{ color: 'white', fontWeight: 300, fontSize: '13px', opacity: 0.95 }}>+</Text>
            <Box style={{
              color: 'white',
              fontWeight: 500, fontSize: '13px',
              background: 'linear-gradient(358deg, rgb(153 153 153), transparent)',
              border: '1px solid #eee',
              marginLeft: '5px',
              marginRight: '5px',
              borderRadius: '5px',
              opacity: 0.75
            }} paddingX={2} paddingY={1}>
              F
            </Box>
            <Text style={{ color: 'white', fontWeight: 300, fontSize: '13px', opacity: 0.95 }}>to access Quick Search (accesible from anywhere in app)</Text>
          </Flex>
        )}
      </Flex>
    </Container >
  )
}

export default withSession(Dashboard)